import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { OutboxMessage } from "@shared-core/data/journey-testing-types";
import { formatPreviewTime } from "@shared-core/domain/preview-clock";
import { Badge } from "../../app/components/primitives/badge";
import { Input } from "../../app/components/primitives/input";
import { Label } from "../../app/components/primitives/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";
import { ListPagination } from "../../app/components/patterns/ListPagination";
import { SlideOverPanel } from "../../app/components/patterns/SlideOverPanel";
import {
  EmptyRow,
  FailedRow,
  LoadingRow,
  describeError,
  useLoaded,
} from "./consoleState";

/**
 * The sandbox an outbox email renders in (06 §4.8): links may open new tabs, nothing else. Never `allow-scripts`
 * or `allow-same-origin` — the staff app's sign-in tokens share this origin.
 */
export const OUTBOX_IFRAME_SANDBOX =
  "allow-popups allow-popups-to-escape-sandbox";

/** Every link in the email opens in a new tab (the frame itself navigates nowhere). */
function withNewTabLinks(html: string): string {
  return `<base target="_blank">${html}`;
}

function asPreformatted(text: string): string {
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return `<pre style="white-space: pre-wrap">${escaped}</pre>`;
}

/** Outbox (07 §1): every email the preview "sent", newest first, by recipient or subject. */
export function OutboxTab({ scenarioRunId }: { scenarioRunId?: string }) {
  const adapter = useDataAdapter();
  const [page, setPage] = useState(1);
  const [to, setTo] = useState("");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const loadOutbox = useCallback(
    () =>
      adapter.listOutboxMessages({
        page,
        pageSize: 25,
        to: to.trim() || undefined,
        q: q.trim() || undefined,
        scenarioRunId,
      }),
    [adapter, page, to, q, scenarioRunId],
  );
  const [outbox, reload] = useLoaded(loadOutbox);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4">
        <div className="grid gap-1.5">
          <Label htmlFor="outbox-to">Recipient</Label>
          <Input
            id="outbox-to"
            className="w-72"
            placeholder="buyer@scenario.example"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="outbox-q">Subject</Label>
          <Input
            id="outbox-q"
            className="w-72"
            placeholder="Search the subject"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </div>
      {outbox.status === "loading" && (
        <LoadingRow label="Loading the outbox…" />
      )}
      {outbox.status === "failed" && (
        <FailedRow message={outbox.message} onRetry={reload} />
      )}
      {outbox.status === "loaded" && outbox.data.total === 0 && (
        <EmptyRow>
          Nothing in the outbox yet — run a scenario or act on a real screen.
        </EmptyRow>
      )}
      {outbox.status === "loaded" && outbox.data.total > 0 && (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>To</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Sent (preview time)</TableHead>
                <TableHead>Scenario</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {outbox.data.items.map((message) => (
                <TableRow
                  key={message.id}
                  className="cursor-pointer"
                  onClick={() => setOpenId(message.id)}
                >
                  <TableCell>{message.to}</TableCell>
                  <TableCell className="font-medium">
                    {message.subject}
                    {message.source === "supplier" && (
                      <Badge className="ml-2" variant="outline">
                        supplier
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>{formatPreviewTime(message.sent_at)}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {message.scenario_run_id ?? "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <ListPagination
            page={outbox.data.page}
            totalPages={outbox.data.totalPages}
            onPageChange={setPage}
          />
        </>
      )}
      <OutboxMessagePanel messageId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}

function OutboxMessagePanel({
  messageId,
  onClose,
}: {
  messageId: string | null;
  onClose: () => void;
}) {
  const adapter = useDataAdapter();
  const [message, setMessage] = useState<OutboxMessage | null>(null);

  useEffect(() => {
    setMessage(null);
    if (!messageId) return;
    let cancelled = false;
    adapter
      .getOutboxMessage(messageId)
      .then((value) => !cancelled && setMessage(value))
      .catch((err: unknown) => !cancelled && toast.error(describeError(err)));
    return () => {
      cancelled = true;
    };
  }, [adapter, messageId]);

  return (
    <SlideOverPanel
      open={messageId !== null}
      onOpenChange={(open) => !open && onClose()}
      title={message?.subject ?? "Email"}
      description={
        message
          ? `To ${message.to} · ${formatPreviewTime(message.sent_at)}`
          : undefined
      }
      className="sm:max-w-3xl"
    >
      {!message ? (
        <LoadingRow label="Loading the email…" />
      ) : (
        <iframe
          title={`Email: ${message.subject}`}
          sandbox={OUTBOX_IFRAME_SANDBOX}
          srcDoc={withNewTabLinks(message.html || asPreformatted(message.text))}
          className="h-[70vh] w-full rounded-md border bg-white"
        />
      )}
    </SlideOverPanel>
  );
}
